import React, { PropsWithChildren, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import GroupIcon from '@material-ui/icons/Group';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import BugReportIcon from '@material-ui/icons/BugReport';
import LinkIcon from '@material-ui/icons/Link';
import StorageIcon from '@material-ui/icons/Storage';
import CodeIcon from '@material-ui/icons/Code';
import TimelineIcon from '@material-ui/icons/Timeline';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import ChatIcon from '@material-ui/icons/Chat';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import SecurityIcon from '@material-ui/icons/Security';
import AssessmentIcon from '@material-ui/icons/Assessment';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { useUserRole } from '../../hooks/useUserRole';
import { DevinChatDialog } from '../DevinChatButton/DevinChatDialog';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import { DocsSidebarMenu } from './DocsSidebarMenu';
import { FloatingSidebarMenu } from './FloatingSidebarMenu';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: ({ isOpen }: { isOpen: boolean }) =>
      isOpen ? sidebarConfig.drawerWidthOpen : sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: ({ isOpen }: { isOpen: boolean }) =>
      isOpen
        ? sidebarConfig.drawerWidthOpen - sidebarConfig.iconPadding * 2
        : sidebarConfig.drawerWidthClosed,
    marginLeft: sidebarConfig.iconPadding,
    display: 'flex',
    alignItems: 'center',
  },
});

const useStyles = makeStyles({
  '@global': {
    '.MuiListItemText-root': {
      whiteSpace: 'pre-line !important',
    },
    '#devin-sidebar-item': {
      '& .MuiListItemIcon-root': {
        color: '#FF6B35 !important',
      },
      '& .MuiListItemText-root': {
        color: '#FF6B35 !important',
        fontWeight: 600,
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 107, 53, 0.08) !important',
      },
    },
  },
});

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();
  const classes = useSidebarLogoStyles({ isOpen });

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const OrangeChatIcon = () => <ChatIcon style={{ color: '#FF6B35' }} />;

const DevinSidebarItem = ({ onClick }: { onClick: () => void }) => {
  return (
    <div id="devin-sidebar-item" style={{ position: 'relative' }}>
      <SidebarItem
        icon={OrangeChatIcon}
        text="Chat con Devin"
        onClick={onClick}
      />
      <style>{`
        #devin-sidebar-item .MuiListItemText-primary {
          color: #FF6B35 !important;
          font-weight: 600 !important;
        }
        #devin-sidebar-item .MuiListItem-root:hover {
          background-color: rgba(255, 107, 53, 0.08) !important;
        }
      `}</style>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { isAdmin, isSecTeam, canAccessDevinUsage } = useUserRole();
  const [openDevinDialog, setOpenDevinDialog] = useState(false);
  useStyles(); // Apply global styles

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarItem icon={LinkIcon} to="shortcuts" text="Shortcuts" />
          <SidebarItem icon={GroupIcon} to="squads" text="Squads" />
          <SidebarItem icon={ExtensionIcon} to="catalog" text="Catálogo" />
          <SidebarItem icon={LibraryBooks} to="libraries" text="Librerías" />
          <SidebarItem
            icon={BugReportIcon}
            to="jira-projects"
            text="Proyectos Jira"
          />
          <SidebarItem icon={StorageIcon} to="eks" text="EKS Clusters" />
          <SidebarGroup label="Kafka" icon={<StorageIcon />}>
            <FloatingSidebarMenu
              icon={StorageIcon}
              id="kafka"
              title="Kafka"
              items={[
                {
                  icon: StorageIcon,
                  title: 'Clusters',
                  description: 'Clústeres de Kafka',
                  to: '/msk-clusters',
                },
                {
                  icon: CodeIcon,
                  title: 'NonProd',
                  description: 'Entorno no productivo',
                  to: '/msk-clusters-nonprod',
                },
                {
                  icon: SecurityIcon,
                  title: 'Prod',
                  description: 'Entorno productivo',
                  to: '/msk-clusters-prod',
                },
                {
                  icon: LibraryBooks,
                  title: 'Event Catalog',
                  description: 'Eventos y esquemas AsyncAPI',
                  to: '/kafka/event-catalog',
                },
              ]}
            />
          </SidebarGroup>
          <SidebarGroup label="Docs" icon={<LibraryBooks />}>
            <DocsSidebarMenu />
          </SidebarGroup>
          <SidebarItem icon={LibraryBooks} to="newsletter" text="Newsletter" />
          <SidebarItem icon={StorageIcon} to="wfp-squads" text="WFP" />
          <SidebarGroup label="Metrics" icon={<TimelineIcon />}>
            <FloatingSidebarMenu
              icon={TimelineIcon}
              id="engineering-metrics"
              title="Métricas"
              items={[
                {
                  icon: AccountBalanceWalletIcon,
                  title: 'FinOps',
                  description: 'Costos de infraestructura',
                  to: '/finops',
                },
                {
                  icon: TimelineIcon,
                  title: 'Métricas DORA',
                  description: 'Entrega de software',
                  to: '/dora-metrics',
                },
                ...(canAccessDevinUsage
                  ? [
                      {
                        icon: AssessmentIcon,
                        title: 'Devin',
                        description: 'Uso y salud de Devin',
                        to: '/devin-usage',
                      },
                    ]
                  : []),
              ]}
            />
          </SidebarGroup>
          {isSecTeam && (
            <SidebarItem icon={SecurityIcon} to="security" text="Security" />
          )}
          <DevinSidebarItem onClick={() => setOpenDevinDialog(true)} />
          {isAdmin && (
            <SidebarItem
              icon={SettingsApplicationsIcon}
              to="management"
              text="Administración"
            />
          )}
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarItem
          icon={HeadsetMicIcon}
          to="platform-support"
          text="Soporte de Plataforma"
        />
        <SidebarGroup
          label="Configuración"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
      <DevinChatDialog
        open={openDevinDialog}
        onClose={() => setOpenDevinDialog(false)}
      />
    </SidebarPage>
  );
};
